import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, AlertTriangle } from "lucide-react";
import { Avatar } from "../ui/Avatar";
import { cn } from "../../utils/cn";
import useHelpdeskTeam, {
  HELPDESK_ROLE_LABELS,
  displayNameOf,
  filterTeam,
  initialsOf,
  roleIdOf,
} from "./useHelpdeskTeam";

// Popover buscable de agentes del equipo para asignar/reasignar conversaciones
// de helpdesk. El fetch del equipo y el filtro de roles viven en
// useHelpdeskTeam (los comparte con las @menciones de las notas privadas).

export default function AgentPicker({ onSelect, onClose, className }) {
  const { t } = useTranslation();
  const { team: agents, error: fetchError, reload: fetchAgents } = useHelpdeskTeam();
  const [query, setQuery] = useState("");
  const panelRef = useRef(null);

  // Cierre por click fuera (Esc se maneja en el onKeyDown del panel, porque
  // el foco vive dentro — el input de búsqueda tiene autoFocus).
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const filtered = useMemo(() => filterTeam(agents, query), [agents, query]);

  const loading = agents === null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t("Seleccionar agente")}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose?.();
        }
      }}
      className={cn(
        "absolute right-0 top-full mt-1 z-30 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-bg shadow-elevated animate-in",
        className,
      )}
    >
      {/* Búsqueda */}
      <div className="p-2 border-b border-border">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-muted pointer-events-none"
            aria-hidden="true"
          />
          <input
            // type="search", not "text": the legacy global rule
            // `input[type="text"]` (_modern-forms.scss) overrides pl-8 and the
            // placeholder ran under the icon. The native clear button is hidden;
            // max-md:text-[16px] keeps the 16px that rule gave phones (no iOS zoom).
            type="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("Buscar por nombre o email…")}
            aria-label={t("Buscar agente")}
            className="w-full h-9 pl-8 pr-3 rounded-md border border-border bg-bg text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-brand-500 max-md:text-[16px] [&::-webkit-search-cancel-button]:appearance-none"
          />
        </div>
      </div>

      {/* Lista */}
      <div className="max-h-64 overflow-y-auto py-1" role="listbox" aria-label={t("Agentes del equipo")}>
        {loading ? (
          <div className="px-2 py-1 space-y-1" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-2">
                <div className="w-7 h-7 rounded-full bg-bg-muted animate-pulse shrink-0" />
                <div className="flex-1 space-y-1">
                  <div className="h-3 w-2/3 rounded bg-bg-muted animate-pulse" />
                  <div className="h-2.5 w-1/3 rounded bg-bg-muted animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : fetchError ? (
          <div className="px-3 py-4 text-center">
            <div className="flex items-center justify-center gap-1.5 text-xs text-status-error">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {t("No se pudo cargar el equipo.")}
            </div>
            <button
              type="button"
              onClick={fetchAgents}
              className="mt-2 h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
            >
              {t("Reintentar")}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-fg-muted">
            {query.trim() ? t("Sin resultados para la búsqueda.") : t("No hay agentes disponibles.")}
          </div>
        ) : (
          filtered.map((u) => {
            const id = u?._id || u?.id;
            return (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected="false"
                onClick={() => onSelect?.(id)}
                className="w-full min-h-[44px] flex items-center gap-2.5 px-3 py-2 text-left hover:bg-bg-muted focus:outline-none focus-visible:bg-bg-muted transition-colors"
              >
                <Avatar size="sm" tone="neutral" initials={initialsOf(u)} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-fg truncate">{displayNameOf(u)}</span>
                  <span className="block text-[11px] text-fg-muted truncate">
                    {t(HELPDESK_ROLE_LABELS[roleIdOf(u)])}
                    {u?.email ? ` · ${u.email}` : ""}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
